import { useState } from 'react';
import { Alert, Button, TextField } from '@territory/ui';
import { apiUrl } from '@territory/client/routes';
import {
  BRAND,
  brandScreen,
  BrandMark,
  BrandWordmark,
  BrandStyleTag,
} from './wedgeBrand';

/**
 * Sets (or resets) a password for a wedge account from an emailed link
 * (wedge.repvantage.io/?reset=<token>).
 *
 * 2026-09-01 · email+password auth was half-broken: the backend reset email
 * pointed at the retired CRM host and the wedge SPA had no screen to land on,
 * so nobody could set or reset a password. This mirrors MagicRedeem: it reads
 * the ?reset= token, collects a new password, and POSTs { token, password } to
 * /api/auth/wedge/password/reset (authService.resetPassword, which also serves
 * the first-time set for an OAuth/magic account that has no password yet).
 *
 * The token is an opaque random hex string (not a JWT), so there is nothing to
 * decode client-side. resetPassword revokes all sessions and does NOT mint a
 * new one, so on success we send the rep to the sign-in door to log in with the
 * password they just set.
 */
export function ResetRedeem({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [focused, setFocused] = useState<'password' | 'confirm' | ''>('');
  const [state, setState] = useState<'form' | 'submitting' | 'done'>('form');
  const [error, setError] = useState('');

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 8 && confirm === password && state !== 'submitting';

  async function submit() {
    if (!canSubmit) return;
    setState('submitting');
    setError('');
    try {
      const res = await fetch(apiUrl('/api/auth/wedge/password/reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        /* The backend answers 400 with a message for an invalid/expired token
           or a too-short password. Surface it; anything unparseable is treated
           as a generic failure. */
        let message = 'That reset link is invalid or has expired. Request a new one from the sign-in screen.';
        try {
          const body = (await res.json()) as { message?: unknown };
          if (typeof body.message === 'string' && body.message.trim()) message = body.message.trim();
        } catch { /* keep default */ }
        setError(message);
        setState('form');
        return;
      }
      setState('done');
    } catch {
      setError('We could not reach the server. Check your connection and try again.');
      setState('form');
    }
  }

  if (state === 'done') {
    return (
      <div style={{ ...brandScreen, gap: 14 }}>
        <BrandStyleTag />
        <BrandMark size={70} />
        <BrandWordmark size={18} />
        <div style={{ fontSize: 18, fontWeight: 700, color: BRAND.text }}>Your password is set</div>
        <div style={{ fontSize: 14, color: BRAND.textSec, maxWidth: 340, lineHeight: 1.5 }}>
          You can now sign in with your email and new password.
        </div>
        <Button
          appearance="wedge-auth"
          authSize="recovery"
          onClick={() => window.location.replace('/')}
          style={{ width: 'auto', minWidth: 200, marginTop: 6 }}
        >
          Go to sign in
        </Button>
      </div>
    );
  }

  return (
    <div style={{ ...brandScreen, gap: 12 }}>
      <BrandStyleTag />
      <BrandMark size={70} />
      <BrandWordmark size={18} />
      <div style={{ fontSize: 18, fontWeight: 700, color: BRAND.text }}>Set your password</div>
      <div style={{ fontSize: 14, color: BRAND.textSec, maxWidth: 340, lineHeight: 1.5, marginBottom: 4 }}>
        Choose a password for your RepVantage account. It must be at least 8 characters.
      </div>
      <div style={{ width: '100%', maxWidth: 340, textAlign: 'left' }}>
        <TextField
          variant="wedge-auth"
          authSize="touch"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setFocused('password')}
          onBlur={() => setFocused('')}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
          placeholder="New password"
          aria-label="New password"
          authFocused={focused === 'password'}
          style={{ marginBottom: tooShort ? 4 : 12 }}
        />
        {tooShort ? <Alert variant="wedge-auth" authLook="field-error" style={{ margin: '2px 0 12px' }}>Use at least 8 characters.</Alert> : null}
        <TextField
          variant="wedge-auth"
          authSize="touch"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onFocus={() => setFocused('confirm')}
          onBlur={() => setFocused('')}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
          placeholder="Confirm password"
          aria-label="Confirm password"
          authFocused={focused === 'confirm'}
          style={{ marginBottom: mismatch ? 4 : 12 }}
        />
        {mismatch ? <Alert variant="wedge-auth" authLook="field-error" style={{ margin: '2px 0 12px' }}>Passwords do not match.</Alert> : null}
        {error ? <Alert variant="wedge-auth" authLook="field-error" style={{ margin: '2px 0 12px' }}>{error}</Alert> : null}
        <Button
          appearance="wedge-auth"
          authSize="touch"
          authState={state === 'submitting' ? 'pending' : canSubmit ? undefined : 'invalid'}
          onClick={() => void submit()}
          disabled={!canSubmit}
        >
          {state === 'submitting' ? 'Setting...' : 'Set password'}
        </Button>
        <Button
          appearance="wedge-auth"
          variant="ghost"
          onClick={() => window.location.replace('/')}
        >
          Back to sign in
        </Button>
      </div>
    </div>
  );
}
